import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Bell, Check, UserPlus, Heart, MessageSquare, TrendingUp, Download, Award, Mail } from "lucide-react";
import Header from "../layouts/header/Header";
import Footer from "../layouts/footer/Footer";
import usePageTitle from "../../hooks/usePageTitle";
import { indexWrapCss, widthCss } from "./css/Home.style";
import { useIsMobile } from "../../hooks/useIsMobile";
import { css } from "@emotion/react";

interface NotificationItem {
    id: number;
    type: string;
    message: string;
    url: string;
    isRead: boolean;
    createdAt: string;
    senderNickname: string;
}

// 백엔드 응답이 isRead 대신 read 필드로 내려오는 경우가 있어 둘 다 확인
const isNotiRead = (item: NotificationItem) => item.isRead ?? (item as any).read ?? false;

const getRelativeTime = (dateStr: string) => {
    const now = new Date();
    const created = new Date(dateStr);
    const diffMs = now.getTime() - created.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);

    if (diffMins < 1) return "방금 전";
    if (diffMins < 60) return `${diffMins}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return "어제";
    if (diffDays < 7) return `${diffDays}일 전`;
    
    return `${created.getMonth() + 1}월 ${created.getDate()}일`;
};

const getDayGroupName = (dateStr: string) => {
    const now = new Date();
    const created = new Date(dateStr);
    
    const isToday = now.getFullYear() === created.getFullYear() &&
                    now.getMonth() === created.getMonth() &&
                    now.getDate() === created.getDate();
                    
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = yesterday.getFullYear() === created.getFullYear() &&
                        yesterday.getMonth() === created.getMonth() &&
                        yesterday.getDate() === created.getDate();
                        
    if (isToday) return "오늘";
    if (isYesterday) return "어제";
    
    return `${created.getFullYear()}년 ${created.getMonth() + 1}월 ${created.getDate()}일`;
};

const NOTIFICATION_ICON_STYLES: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
    FOLLOW: { bg: "bg-[#f3ebfa]", text: "text-[#a855f7]", icon: <UserPlus className="w-5 h-5" /> },
    BOOK_LIKE: { bg: "bg-[#fce7f3]", text: "text-[#f43f5e]", icon: <Heart className="w-5 h-5" /> },
    POSTCARD_LIKE: { bg: "bg-[#fce7f3]", text: "text-[#f43f5e]", icon: <Heart className="w-5 h-5" /> },
    COMMENT_LIKE: { bg: "bg-[#fce7f3]", text: "text-[#f43f5e]", icon: <Heart className="w-5 h-5" /> },
    LIKE: { bg: "bg-[#fce7f3]", text: "text-[#f43f5e]", icon: <Heart className="w-5 h-5" /> },
    COMMENT: { bg: "bg-[#dbeafe]", text: "text-[#0284c7]", icon: <MessageSquare className="w-5 h-5" /> },
    REPLY: { bg: "bg-[#dbeafe]", text: "text-[#0284c7]", icon: <MessageSquare className="w-5 h-5" /> },
    POSTCARD_DOWNLOAD: { bg: "bg-[#f1f5f9]", text: "text-[#64748b]", icon: <Download className="w-5 h-5" /> },
    DOWNLOAD: { bg: "bg-[#f1f5f9]", text: "text-[#64748b]", icon: <Download className="w-5 h-5" /> },
    AUTHOR_NOTE_VIEW_MILESTONE: { bg: "bg-[#ffedd5]", text: "text-[#f97316]", icon: <Award className="w-5 h-5" /> },
    SYSTEM: { bg: "bg-[#ffedd4]", text: "text-[#f97316]", icon: <TrendingUp className="w-5 h-5" /> },
};

const renderIcon = (type: string, isRead: boolean) => {
    const style = NOTIFICATION_ICON_STYLES[type.toUpperCase()] ?? NOTIFICATION_ICON_STYLES.SYSTEM;
    const bgClass = isRead ? "bg-[#f1f5f9]" : style.bg;
    const textClass = isRead ? "text-[#94a3b8]" : style.text;
    return (
        <div className={`w-11 h-11 rounded-[14px] flex items-center justify-center flex-shrink-0 ${bgClass} ${textClass}`}>
            {style.icon}
        </div>
    );
};

const NotificationPage: React.FC = () => {
    usePageTitle("알림함 | 북밋", "회원님의 새로운 알림 목록을 확인해 보세요.");
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const token = localStorage.getItem("jwtToken");

    const mobilePaddingOverrideCss = css`
        @media (max-width: 768px) {
            padding-top: 24px !important;
            padding-bottom: 32px !important;
            min-width: unset !important;
            width: 100%;
        }
    `;

    const desktopPaddingOverrideCss = css`
        padding-top: 24px !important;
        padding-bottom: 64px !important;
    `;

    const [notifications, setNotifications] = useState<NotificationItem[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [activeTab, setActiveTab] = useState<"all" | "unread">("all");
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    // 이메일 설정 상태
    const [emailEnabled, setEmailEnabled] = useState(false);

    // 이메일 알림 설정 패칭
    const fetchEmailSettings = useCallback(async () => {
        if (!token) return;
        try {
            const res = await axios.get("/api/notifications/settings", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (res.data && typeof res.data.emailEnabled === "boolean") {
                setEmailEnabled(res.data.emailEnabled);
            }
        } catch (error) {
            console.error("Failed to fetch email settings:", error);
        }
    }, [token]);

    // 이메일 토글 변경
    const handleToggleEmail = async () => {
        if (!token) return;
        const nextVal = !emailEnabled;
        try {
            await axios.put(
                "/api/notifications/settings",
                { emailEnabled: nextVal },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setEmailEnabled(nextVal);
        } catch (error) {
            console.error("Failed to update email settings:", error);
        }
    };

    // 비로그인 접근 차단
    useEffect(() => {
        if (!token) {
            alert("로그인이 필요한 페이지입니다.");
            navigate("/", { replace: true });
        }
    }, [token, navigate]);

    // 알림 목록 조회 (최초 및 무한 스크롤)
    const fetchNotifications = useCallback(async (pageNum: number, isInitial = false) => {
        if (!token) return;
        setLoading(true);
        try {
            const res = await axios.post(
                "/api/notifications/scroll",
                { 
                    page: pageNum,
                    unreadOnly: activeTab === "unread"
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const fetchedData = res.data?.notifications?.content || [];
            const isLastPage = res.data?.notifications?.last ?? true;

            setNotifications(prev => {
                const combined = isInitial ? fetchedData : [...prev, ...fetchedData];
                // 중복 식별자(id) 제거
                const unique = combined.filter(
                    (item: NotificationItem, idx: number, self: NotificationItem[]) =>
                        self.findIndex(t => t.id === item.id) === idx
                );
                return unique;
            });

            // 읽지 않은 개수 카운트
            if (isInitial) {
                const count = res.data?.notifications?.totalElements || 0;
                // 실제 클라이언트 데이터 기준으로 미확인 알림 수 계산
                // (일반적인 스크롤 API에서는 totalElements가 전체 개수일 수 있으므로 filter로 세팅)
            }

            setHasMore(!isLastPage && fetchedData.length > 0);
        } catch (error) {
            console.error("Failed to load notifications:", error);
        } finally {
            setLoading(false);
        }
    }, [token, activeTab]);

    // 전체 개수 동기화용 (10개 조회 API 호출하여 미확인 상태 가져오기)
    const updateUnreadCount = useCallback(async () => {
        if (!token) return;
        try {
            const res = await axios.post(
                "/api/notifications/list",
                null,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const list = res.data?.notifications?.content || [];
            const unread = list.filter((n: any) => !isNotiRead(n)).length;
            setUnreadCount(unread);
        } catch (error) {
            console.error("Failed to get unread count:", error);
        }
    }, [token]);

    useEffect(() => {
        if (token) {
            fetchNotifications(1, true);
            updateUnreadCount();
            fetchEmailSettings();
        }
    }, [token, fetchNotifications, updateUnreadCount, fetchEmailSettings]);

    // 페이지 변경 시 fetch 실행
    useEffect(() => {
        if (page > 1) {
            fetchNotifications(page, false);
        }
    }, [page, fetchNotifications]);

    // 탭 변경 시 리스트 초기화 및 재조회
    const handleTabChange = (tabType: "all" | "unread") => {
        setActiveTab(tabType);
        setPage(1);
        setHasMore(true);
        fetchNotifications(1, true);
    };

    // 무한 스크롤 감지용 Observer 설정
    const observerRef = useRef<IntersectionObserver | null>(null);
    const lastElementRef = useCallback((node: HTMLDivElement | null) => {
        if (loading) return;
        if (observerRef.current) observerRef.current.disconnect();

        observerRef.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prev => prev + 1);
            }
        });

        if (node) observerRef.current.observe(node);
    }, [loading, hasMore]);

    // 개별 알림 읽음 처리
    const handleNotificationClick = async (item: NotificationItem) => {
        if (!token) return;
        
        if (!isNotiRead(item)) {
            try {
                await axios.post(
                    "/api/notifications/read",
                    { notiId: String(item.id) },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                // 목록 상태 업데이트
                setNotifications(prev =>
                    prev.map(n => (n.id === item.id ? { ...n, isRead: true, read: true } : n))
                );
                setUnreadCount(prev => Math.max(0, prev - 1));
            } catch (error) {
                console.error("Failed to read notification:", error);
            }
        }

        // 지정된 URL로 이동
        if (item.url) {
            navigate(item.url);
        }
    };

    // 모두 읽음 처리
    const handleReadAll = async () => {
        if (!token || notifications.length === 0) return;
        
        const unreadItems = notifications.filter(n => !isNotiRead(n));
        if (unreadItems.length === 0) return;

        try {
            // 백엔드 전체 읽음 처리 API 호출
            await axios.post(
                "/api/notifications/read-all",
                null,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            // 전체 상태 업데이트
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true, read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error("Failed to read all notifications:", error);
        }
    };

    // 현재 탭 필터링 적용된 알림 목록
    const filteredNotifications = notifications.filter(n => {
        if (activeTab === "unread") return !isNotiRead(n);
        return true;
    });

    // 날짜별 그룹핑
    const groupedNotifications = (() => {
        const groups: { [key: string]: NotificationItem[] } = {};
        filteredNotifications.forEach(item => {
            const groupName = getDayGroupName(item.createdAt);
            if (!groups[groupName]) {
                groups[groupName] = [];
            }
            groups[groupName].push(item);
        });
        return groups;
    })();

    // 그룹 순서 (오늘 -> 어제 -> 이전 날짜 순)
    const sortedGroupKeys = Object.keys(groupedNotifications).sort((a, b) => {
        if (a === "오늘") return -1;
        if (b === "오늘") return 1;
        if (a === "어제") return -1;
        if (b === "어제") return 1;
        return b.localeCompare(a); // 내림차순 정렬
    });

    return (
        <div css={indexWrapCss} className="min-w-[320px]">
            <Header searchValue="" onChange={() => {}} name="search" activeFlag="" />

            <main className="bg-[#FAFBFB] min-h-[calc(100vh-80px)]">
                {/* 상단 타이틀 바 + 탭 (스크롤 시 상단 고정) */}
                <div className="sticky top-0 z-10 bg-white border-b border-[#e5e7eb] shadow-[0px_1px_1.5px_rgba(0,0,0,0.06)]">
                    <div className="w-full max-w-[1040px] mx-auto flex items-center justify-between px-6 py-5 md:px-4 md:py-4">
                        <div className="flex items-center gap-4 md:gap-2">
                            <button
                                onClick={() => navigate(-1)}
                                className="w-[38px] h-[38px] md:w-9 md:h-9 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors flex-shrink-0"
                            >
                                <ArrowLeft className="w-5 h-5 text-[#555]" />
                            </button>
                            <div className="flex items-center gap-2.5 md:gap-2">
                                <Bell className="w-5 h-5 md:w-[18px] md:h-[18px] text-[#101828] flex-shrink-0" />
                                <span className="text-[20px] md:text-[18px] font-bold text-[#101828] leading-[30px] md:leading-[27px] whitespace-nowrap">
                                    알림
                                </span>
                                {unreadCount > 0 && (
                                    <span className="bg-[#19CF7A] text-white text-[12px] md:text-[11px] font-bold min-w-[24px] md:min-w-[22px] h-6 md:h-[22px] px-2 md:px-1.5 rounded-full flex items-center justify-center flex-shrink-0">
                                        {unreadCount}
                                    </span>
                                )}
                            </div>
                        </div>
                        {unreadCount > 0 && (
                            <button
                                onClick={handleReadAll}
                                className="flex items-center gap-1.5 md:gap-1 text-[13px] md:text-[12px] font-semibold text-[#19CF7A] bg-[rgba(25,207,122,0.1)] px-3.5 md:px-2.5 py-[7px] md:py-[6px] rounded-[10px] hover:bg-[rgba(25,207,122,0.18)] transition-colors flex-shrink-0 whitespace-nowrap"
                            >
                                <Check className="w-[15px] h-[15px] md:w-[13px] md:h-[13px]" />
                                모두 읽음
                            </button>
                        )}
                    </div>

                    <div className="w-full max-w-[1040px] mx-auto flex items-center justify-between px-6 md:px-4">
                        <div className="flex gap-1">
                            <button
                                onClick={() => handleTabChange("all")}
                                className={`px-4 md:px-3 py-2 text-[14px] md:text-[13px] font-semibold rounded-t-[10px] transition-colors whitespace-nowrap ${
                                    activeTab === "all"
                                        ? "text-[#19CF7A] bg-[rgba(25,207,122,0.1)] border-b border-[#19cf7a]"
                                        : "text-[#6a7282] hover:text-[#555]"
                                }`}
                            >
                                전체
                            </button>
                            <button
                                onClick={() => handleTabChange("unread")}
                                className={`px-4 md:px-3 py-2 text-[14px] md:text-[13px] font-semibold rounded-t-[10px] transition-colors whitespace-nowrap ${
                                    activeTab === "unread"
                                        ? "text-[#19CF7A] bg-[rgba(25,207,122,0.1)] border-b border-[#19cf7a]"
                                        : "text-[#6a7282] hover:text-[#555]"
                                }`}
                            >
                                읽지 않음 {unreadCount > 0 && `(${unreadCount})`}
                            </button>
                        </div>

                        {/* 이메일 수신 동의 토글 */}
                        <div className="flex items-center gap-1.5 md:gap-2 pb-1.5">
                            <Mail className={`w-4 h-4 ${emailEnabled ? "text-[#19CF7A]" : "text-gray-400"}`} />
                            <span className="text-[13px] md:text-[12px] text-[#4B5563] font-medium hidden sm:inline">이메일로도 알림 받기</span>
                            <span className="text-[12px] text-[#4B5563] font-medium sm:hidden">이메일 알림</span>
                            <button
                                onClick={handleToggleEmail}
                                className={`relative inline-flex h-5 w-9 md:h-[22px] md:w-10 items-center rounded-full transition-colors duration-200 ${
                                    emailEnabled ? "bg-[#19CF7A]" : "bg-[#E5E7EB]"
                                }`}
                            >
                                <span
                                    className={`inline-block h-3.5 w-3.5 md:h-4 md:w-4 transform rounded-full bg-white transition-transform duration-200 ${
                                        emailEnabled ? "translate-x-4 md:translate-x-5" : "translate-x-1"
                                    }`}
                                />
                            </button>
                        </div>
                    </div>
                </div>

                <div css={[widthCss, isMobile ? mobilePaddingOverrideCss : desktopPaddingOverrideCss]}>
                    <div className="w-full max-w-[1040px] mx-auto px-6 md:px-4">
                    {/* 알림 리스트 (A. 배지 리스트 단일 레이아웃) */}
                    <div className="flex flex-col gap-8">
                        {filteredNotifications.length === 0 ? (
                            <div className="bg-white border border-[#E5ECE8] rounded-[24px] py-24 flex flex-col items-center justify-center shadow-sm">
                                <div className="w-16 h-16 bg-[#F5F7F6] rounded-full flex items-center justify-center text-3xl mb-4">
                                    🔔
                                </div>
                                <p className="text-[#888] text-[15px] font-medium">새로운 알림이 없습니다.</p>
                            </div>
                        ) : (
                            sortedGroupKeys.map(groupName => (
                                <div key={groupName} className="flex flex-col gap-4">
                                    {/* 날짜 구분선 */}
                                    <div className="text-[14px] font-extrabold text-[#888] px-1">
                                        {groupName}
                                    </div>

                                    {/* 카드 컨테이너 */}
                                    <div 
                                        className="bg-white border border-[#E5ECE8] rounded-[24px] overflow-hidden shadow-[0px_4px_20px_rgba(0,0,0,0.02)] divide-y divide-[#F5F7F6]"
                                    >
                                        {groupedNotifications[groupName].map((item, idx) => {
                                            const isLastItem = idx === groupedNotifications[groupName].length - 1;
                                            const itemIsRead = isNotiRead(item);
                                            return (
                                                <div
                                                    key={item.id}
                                                    ref={isLastItem ? lastElementRef : null}
                                                    onClick={() => handleNotificationClick(item)}
                                                    className={`group flex items-start gap-4 px-5 py-[18px] hover:bg-[#F9FAF9] cursor-pointer transition-all duration-200 ${
                                                        !itemIsRead ? "bg-[#F4FBF7]" : ""
                                                    }`}
                                                >
                                                    {/* 알림 유형별 아이콘 */}
                                                    {renderIcon(item.type, itemIsRead)}

                                                    {/* 본문 메시지 & 상대적 시간 */}
                                                    <div className="flex-1 min-w-0 flex flex-col gap-[5px] pt-0.5">
                                                        <p className={`text-[14.5px] leading-[21px] group-hover:text-[#19CF7A] transition-colors break-keep ${
                                                            itemIsRead ? "text-[#62748e] font-normal" : "text-[#1d293d] font-medium"
                                                        }`}>
                                                            {item.message}
                                                        </p>
                                                        <span className="text-[#90a1b9] text-[12px]">
                                                            {getRelativeTime(item.createdAt)}
                                                        </span>
                                                    </div>

                                                    {/* 읽지 않음 동그라미 배지 */}
                                                    {!itemIsRead && (
                                                        <div className="flex items-start pl-2 pt-4 shrink-0">
                                                            <div className="w-[7px] h-[7px] rounded-full bg-[#19CF7A] shadow-[0px_1px_3px_rgba(0,0,0,0.1)]" />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))
                        )}
                        
                        {loading && (
                            <div className="flex justify-center items-center gap-1.5 py-4">
                                <div className="w-[6px] h-[6px] rounded-full bg-[#d1d5dc] opacity-50" />
                                <div className="w-[6px] h-[6px] rounded-full bg-[#d1d5dc] opacity-70" />
                                <div className="w-[6px] h-[6px] rounded-full bg-[#d1d5dc] opacity-90" />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>

            <Footer />
        </div>
    );
};

export default NotificationPage;
